import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { Box, IconButton } from "@mui/material";
import { useFormik } from "formik";
import { useState } from "react";
import * as Yup from "yup";
import Flex from "../../../../../components/Flex";
import SideSheetDrawer from "../../../../../components/SidesheetDrawer";
import Text from "../../../../../components/Text";
import DropZone from "../../../../../components/app/DropZone";
import StyledButtonV2 from "../../../../../components/app/StyledButtonV2";
import { useQueryClient } from "@tanstack/react-query";
import { DisclosureHost } from "../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { useUploadEra } from "../../../../../queries/billings/era/use-upload-era";
import { ALIAS } from "../../../../../tokens/design-tokens";
import { showToastOnApiCall } from "../../../../../utils/utils";

interface UploadEraEobModalProps {
  disclosureHost: DisclosureHost;
}

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB in bytes
const ALLOWED_EXTENSIONS = [".txt", ".835", ".edi"];

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const UploadEraEobModal = ({ disclosureHost }: UploadEraEobModalProps) => {
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const { mutateAsync: uploadEra } = useUploadEra();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const formik = useFormik<{ files: FileList | null }>({
    initialValues: {
      files: null,
    },
    validationSchema: Yup.object({
      files: Yup.mixed()
        .required("Please select at least one file")
        .test(
          "fileType",
          "Only .txt, .835, or .edi files are allowed",
          (value: any) => {
            if (!value || !value.length) return false;
            const files = Array.from(value as FileList);
            return files.every((file) => {
              const fileName = file.name.toLowerCase();
              return ALLOWED_EXTENSIONS.some((ext) => fileName.endsWith(ext));
            });
          }
        )
        .test(
          "fileSize",
          "Total file size must not exceed 20MB",
          (value: any) => {
            if (!value || !value.length) return false;
            const files = Array.from(value as FileList);
            const totalSize = files.reduce((sum, file) => sum + file.size, 0);
            return totalSize <= MAX_FILE_SIZE;
          }
        ),
    }),
    onSubmit: (values) => {
      const files = Array.from(values.files ?? []);
      if (files.length === 0) return;
      // The upload-835 endpoint accepts a single file, so upload each selected
      // file and surface the last response in the success toast.
      showToastOnApiCall({
        apiCall: Promise.all(files.map((file) => uploadEra({ file }))).then(
          (responses) => responses[responses.length - 1]
        ),
        showSnackbar,
        onSave: () => {
          queryClient.invalidateQueries({ queryKey: ["get-all-eras"] });
          closeAndReset();
        },
      });
    },
  });

  const handleFileSelect = (fileList: FileList | null) => {
    formik.setFieldTouched("files", true, false);
    if (fileList && fileList.length > 0) {
      const files = Array.from(fileList);
      setSelectedFiles(files);
      formik.setFieldValue("files", fileList, true);
    } else {
      setSelectedFiles([]);
      formik.setFieldValue("files", null, true);
    }
  };

  const handleRemoveFile = (index: number) => {
    const next = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(next);
    if (next.length === 0) {
      formik.setFieldValue("files", null, true);
    } else {
      const dt = new DataTransfer();
      next.forEach((f) => dt.items.add(f));
      formik.setFieldValue("files", dt.files, true);
    }
  };

  function closeAndReset() {
    formik.resetForm();
    setSelectedFiles([]);
    disclosureHost.close();
  }

  return (
    <SideSheetDrawer
      open={disclosureHost.opened}
      onClose={closeAndReset}
      title="Upload ERA (ANSI 835 Files)"
      withCloseIcon={false}
      endIcon={
        <IconButton size="small" onClick={closeAndReset}>
          <CloseIcon sx={{ fontSize: 18, color: ALIAS.text.default }} />
        </IconButton>
      }
      footerActions={
        <StyledButtonV2
          label="Upload"
          variant="contained"
          onClick={formik.submitForm}
          sx={{ width: "100px" }}
          disabled={!formik.values.files}
        />
      }
    >
      <Box>
        <Text mb={2} variant="body2" color="text.secondary">
          ANSI 835 files with following extension, .txt or .835 or .edi can be
          uploaded here. Total size of the uploaded files cannot be more than
          20MB.
        </Text>

        {selectedFiles.length === 0 ? (
          <DropZone
            multiple={true}
            height="240px"
            accept=".txt,.835,.edi"
            content="Drag & Drop files Or Browse Files"
            onClick={handleFileSelect}
            error={formik.touched.files && formik.errors.files}
            supportedFile=".txt, .835, .edi"
          />
        ) : (
          <Flex flexDirection="column" gap="8px">
            {selectedFiles.map((file, index) => (
              <Flex
                key={`${file.name}-${index}`}
                alignItems="center"
                gap="12px"
                sx={{
                  p: "12px",
                  border: `1px solid ${ALIAS.outline.barelyGrey}`,
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                }}
              >
                <Flex
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "8px",
                    backgroundColor: ALIAS.surface.action.secondaryBg.blue,
                    flexShrink: 0,
                  }}
                >
                  <InsertDriveFileOutlinedIcon
                    sx={{ fontSize: 18, color: ALIAS.outline.lightBlue }}
                  />
                </Flex>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Text
                    size="14px"
                    fontWeight={500}
                    c={ALIAS.text.darkGrey}
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {file.name}
                  </Text>
                  <Text size="12px" c={ALIAS.text.default} mt="2px">
                    {formatFileSize(file.size)} – 100% uploaded
                  </Text>
                </Box>

                <IconButton
                  size="small"
                  onClick={() => handleRemoveFile(index)}
                  aria-label="Remove file"
                >
                  <RadioButtonUncheckedIcon
                    sx={{ fontSize: 20, color: ALIAS.text.default }}
                  />
                </IconButton>
              </Flex>
            ))}

            {formik.touched.files && formik.errors.files && (
              <Text size="12px" c="error.main" mt="4px">
                {formik.errors.files as string}
              </Text>
            )}
          </Flex>
        )}
      </Box>
    </SideSheetDrawer>
  );
};

export default UploadEraEobModal;
